import React, { useEffect, useState, useRef } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    FormControl,
    Button,
    TextField,
    Autocomplete,
    InputAdornment,
    styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

import { addDays } from "date-fns";
import { DateRangePicker, RangeKeyDict, Range } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export interface IFilters {
    selectedId: string;
    startDate: string | null;
    endDate: string | null;
    selectedTxnStatus: string;
    selectedTxnType: string;
    selectedUserId: string;
    selectedNetworkType: string;
    selectedMultiCountryIds: string;
}

interface ReusableDrawerProps {
    open: boolean;
    onClose: () => void;
    onApply: (filters: IFilters) => void;
    fetchDropdownOptions?: () => Promise<any[]>;
    fetchCountriesWithTransactions?: () => Promise<any[]>;
    fetchTxnStatusDropdownOptions?: () => Promise<any[]>;
    fetchTxnTypeDropdownOptions?: () => Promise<any[]>;
    fetchUserNameDropdownOptions?: () => Promise<any[]>;
    fetchNetworkTypeDropdownOptions?: () => Promise<any[]>;
    title?: string;
    defaultValue?: string;
    defaultStartDate?: string | null;
    defaultEndDate?: string | null;
    showDateRange?: boolean;
    showDateTimeRangeFilter?: boolean;
    showUserNameFilter?: boolean;
    defaultUserName?: string;
    defaultTxnStatus?: string;
    showCountryFilter?: boolean;
    showTransactionLogCountryFilter?: boolean;
    showTxnStatusFilter?: boolean;
    defaultTxnType?: string;
    showMultiCountryFilter?: boolean;
    showNetworkTypeFilter?: boolean;
    defaultNetworkType?: string;
    showTxnTypeFilter?: boolean;
    onFiltersCountChange?: (count: number) => void;
}

const ReusableDrawer: React.FC<ReusableDrawerProps> = ({
    open,
    onClose,
    onApply,
    fetchDropdownOptions,
    fetchCountriesWithTransactions,
    fetchTxnStatusDropdownOptions,
    fetchTxnTypeDropdownOptions,
    fetchUserNameDropdownOptions,
    fetchNetworkTypeDropdownOptions,
    title = "Filter Options",
    defaultValue = "",
    defaultStartDate = null,
    defaultEndDate = null,
    showDateRange = false,
    showDateTimeRangeFilter = false,
    showUserNameFilter = false,
    defaultUserName = "",
    defaultTxnStatus = "",
    showCountryFilter = true,
    showTransactionLogCountryFilter = false,
    showTxnStatusFilter = false,
    defaultTxnType = "",
    showMultiCountryFilter = true,
    showNetworkTypeFilter = false,
    defaultNetworkType = "",
    showTxnTypeFilter = false,
    onFiltersCountChange
}) => {
    const [options, setOptions] = useState<any[]>([]);
    const [selectedValue, setSelectedValue] = useState<string>("");

    // Date range is null by default; only set when user selects
    const [dateRange, setDateRange] = useState<Range | null>(
        defaultStartDate && defaultEndDate
            ? {
                startDate: new Date(defaultStartDate),
                endDate: new Date(defaultEndDate),
                key: "selection",
            }
            : null
    );

    const [calendarOpen, setCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement | null>(null);

    const [dateTimeOpen, setDateTimeOpen] = useState(false);
    const dateTimeRef = useRef<HTMLDivElement | null>(null);

    // time
    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("23:59");

    // Transaction status value
    const [txnStatusOptions, setTxnStatusOptions] = useState<any[]>([]);
    const [selectedTxnStatus, setSelectedTxnStatus] = useState<string>("");

    // Transaction type value
    const [txnTypeOptions, setTxnTypeOptions] = useState<any[]>([]);
    const [selectedTxnType, setSelectedTxnType] = useState<string>("");

    // Transaction type value
    const [userNameOptions, setUserNameOptions] = useState<any[]>([]);
    const [selectedUserId, setselectedUserId] = useState<string>("");

    // Network type value
    const [networkTypeOptions, setNetworkTypeOptions] = useState<any[]>([]);
    const [selectedNetworkType, setSelectedNetworkType] = useState<string>("");


    // Country multi select
    const [selectedCountriesMulti, setSelectedCountriesMulti] = useState<string[]>([]);

    useEffect(() => {
        // Assigns country list based on showTransactionLogCountryFilter flag
        // - If true, loads only countries with recorded transactions
        // - If false, fetches all active countries
        const fetchCountries = showTransactionLogCountryFilter ? fetchCountriesWithTransactions : fetchDropdownOptions;

        if (open && selectedValue == '' && fetchCountries) {
            fetchCountries()
                .then((data) => {
                    const modifiedOptions = [
                        { countryName: "All Countries", countryId: "" },
                        ...data
                    ];
                    setOptions(modifiedOptions);
                    setSelectedValue(defaultValue || "");
                })
                .catch(console.error);
        }
        if (open && selectedTxnStatus == '' && fetchTxnStatusDropdownOptions) {
            fetchTxnStatusDropdownOptions()
                .then((data) => {
                    const modifiedOptions = [
                        { statusName: "All", statusId: "" },
                        ...data
                    ];
                    setTxnStatusOptions(modifiedOptions);
                    setSelectedTxnStatus(defaultTxnStatus);
                })
                .catch(console.error);
        }
        if (open && selectedTxnType == '' && fetchTxnTypeDropdownOptions) {
            fetchTxnTypeDropdownOptions()
                .then((data) => {
                    const modifiedOptions = [
                        { name: "All", id: "" },
                        ...data
                    ];
                    setTxnTypeOptions(modifiedOptions);
                    setSelectedTxnType(defaultTxnType);
                })
                .catch(console.error);
        }
        if (open && selectedUserId == '' && fetchUserNameDropdownOptions) {
            fetchUserNameDropdownOptions()
                .then((data) => {
                    const modifiedOptions = [
                        { name: "All", id: "" },
                        ...data
                    ];
                    setUserNameOptions(modifiedOptions);
                    setselectedUserId(defaultUserName);
                })
                .catch(console.error);
        }
        if (open && selectedNetworkType == '' && fetchNetworkTypeDropdownOptions) {
            fetchNetworkTypeDropdownOptions()
                .then((data) => {
                    const modifiedOptions = [
                        { networkType: "All", id: "" },
                        ...data
                    ];
                    setNetworkTypeOptions(modifiedOptions);
                    setSelectedNetworkType(defaultNetworkType);
                })
                .catch(console.error);
        }
    }, [open, showTransactionLogCountryFilter, defaultValue, fetchTxnStatusDropdownOptions, defaultTxnStatus, fetchTxnTypeDropdownOptions, defaultTxnType, fetchNetworkTypeDropdownOptions, defaultTxnType, fetchUserNameDropdownOptions, defaultUserName]);

    // Only count applied filters
    const getAppliedFiltersCount = () => {
        let count = 0;
        if (selectedValue) count += 1;
        if (showDateRange && dateRange) count += 1;
        if (showTxnStatusFilter && selectedTxnStatus) count += 1;
        if (showTxnTypeFilter && selectedTxnType) count += 1;

        //User Log page filters
        if (showUserNameFilter && dateRange) count += 1;
        if (showDateTimeRangeFilter && dateRange) count += 1;

        //Network page filter
        if (showNetworkTypeFilter && selectedNetworkType) count += 1;
        if (showMultiCountryFilter && selectedCountriesMulti.length > 0) count += 1;
        return count;
    };


    // Apply filters
    const handleApply = () => {
        const appliedCount = getAppliedFiltersCount();

        let formattedStartDate: string | null = null;
        let formattedEndDate: string | null = null;

        if (dateRange?.startDate && dateRange?.endDate) {
            if (showDateTimeRangeFilter) {
                formattedStartDate = moment(dateRange.startDate)
                    .set({
                        hour: parseInt(startTime.split(":")[0]),
                        minute: parseInt(startTime.split(":")[1]),
                        second: 0,
                    })
                    .format("YYYY-MM-DD HH:mm:ss");

                formattedEndDate = moment(dateRange.endDate)
                    .set({
                        hour: parseInt(endTime.split(":")[0]),
                        minute: parseInt(endTime.split(":")[1]),
                        second: 59,
                    })
                    .format("YYYY-MM-DD HH:mm:ss");
            } else {
                formattedStartDate = moment(dateRange.startDate)
                    .startOf("day")
                    .format("YYYY-MM-DD HH:mm:ss");

                formattedEndDate = moment(dateRange.endDate)
                    .endOf("day")
                    .format("YYYY-MM-DD HH:mm:ss");
            }
        }

        onApply({
            selectedId: selectedValue,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            selectedTxnStatus: selectedTxnStatus,
            selectedTxnType: selectedTxnType,
            selectedUserId: selectedUserId,
            selectedNetworkType: selectedNetworkType,
            selectedMultiCountryIds: selectedCountriesMulti.join(","),
        });
        onFiltersCountChange?.(appliedCount); // pass count to parent
        onClose();
    };


    const handleClear = () => {
        setSelectedValue("");
        setDateRange(null);
        onApply({ selectedId: "", startDate: null, endDate: null, selectedTxnStatus: "", selectedTxnType: "", selectedUserId: "", selectedNetworkType: "", selectedMultiCountryIds: "" });
        onFiltersCountChange?.(0);
        onClose();
    };

    // Clear only date range
    const handleClearDate = () => {
        setDateRange(null);
        onFiltersCountChange?.(getAppliedFiltersCount() - 1);
    };

    const handleClearDateTime = () => {
        setDateRange(null);
        setStartTime("00:00");
        setEndTime("23:59");
    };

    const StyledButton = styled(Button)(({ theme }) => ({
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        width: "45%",
        borderRadius: "8px",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main,
            opacity: 0.9,
        },
    }));

    const handleSelect = (ranges: RangeKeyDict) => {
        const selection = ranges.selection;
        if (selection.startDate && selection.endDate) {
            setDateRange({
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: "selection",
            });
            onFiltersCountChange?.(getAppliedFiltersCount());
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
            setCalendarOpen(false);
        }
    };

    useEffect(() => {
        if (calendarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarOpen]);

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 380, height: "100%", display: "flex", flexDirection: "column", padding: 0 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderBottom: "1px solid #ddd",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 20,
                    }}
                >
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Scrollable Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {/* Country Dropdown */}
                    {(showCountryFilter && <FormControl fullWidth>
                        <Autocomplete
                            fullWidth
                            options={options}
                            getOptionLabel={(option) => option.countryName}
                            value={options.find((opt) => opt.countryId === selectedValue) || null}
                            onChange={(event, newValue) => {
                                // setSelectedValue(newValue ? newValue.currencyCode : "");
                                setSelectedValue(newValue ? newValue.countryId : "");
                                onFiltersCountChange?.(getAppliedFiltersCount());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Country"
                                    variant="outlined"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleApply();
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormControl>)}

                    {/* Date Range */}
                    {showDateRange && (
                        <Box sx={{ position: "relative", mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Select Date Range"
                                value={
                                    dateRange
                                        ? `${dateRange.startDate?.toLocaleDateString()} - ${dateRange.endDate?.toLocaleDateString()}`
                                        : ""
                                }
                                onClick={() => setCalendarOpen(!calendarOpen)}
                                placeholder="Select a date range"
                                InputProps={{
                                    endAdornment: dateRange ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent toggling calendar
                                                    handleClearDate();
                                                }}
                                                edge="end"
                                                size="small"
                                                aria-label="Clear date range"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            {calendarOpen && (
                                <Box
                                    ref={calendarRef}
                                    sx={{
                                        position: "absolute",
                                        zIndex: 9999,
                                        top: "62px",
                                        left: 0,
                                        backgroundColor: "#fff",
                                        boxShadow: 3,
                                    }}
                                >
                                    <DateRangePicker
                                        ranges={[dateRange || { startDate: new Date(), endDate: new Date(), key: "selection" }]}
                                        onChange={handleSelect}
                                        months={1}
                                        minDate={new Date(2024, 0, 1)}
                                        maxDate={addDays(new Date(), 30)}
                                        direction="horizontal"
                                        scroll={{ enabled: true }}
                                    />

                                    <Box sx={{ mt: 1, mb: 1, display: "flex", justifyContent: "center" }}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            disabled={!dateRange}
                                            onClick={() => setCalendarOpen(false)}
                                        >
                                            Select
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Transaction Status Dropdown */}
                    {showTxnStatusFilter && (
                        <FormControl fullWidth>
                            <Autocomplete
                                fullWidth
                                options={txnStatusOptions}
                                getOptionLabel={(option) => option.statusName}
                                value={txnStatusOptions.find((opt) => opt.statusId === selectedTxnStatus) || null}
                                onChange={(event, newValue) => {
                                    // setSelectedValue(newValue ? newValue.currencyCode : "");
                                    setSelectedTxnStatus(newValue ? newValue.statusId : "");
                                    onFiltersCountChange?.(getAppliedFiltersCount());
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Transaction Status"
                                        variant="outlined"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleApply();
                                            }
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    )}

                    {/* Multi select country Dropdown */}
                    {showMultiCountryFilter && (
                        <FormControl fullWidth>
                            <Autocomplete
                                multiple
                                fullWidth
                                options={options}
                                getOptionLabel={(option) => option.countryName}
                                value={options.filter((opt) =>
                                    selectedCountriesMulti.includes(opt.countryId)
                                )}
                                onChange={(event, newValue) => {
                                    const ids = newValue.map((item) => item.countryId);
                                    setSelectedCountriesMulti(ids);
                                    onFiltersCountChange?.(getAppliedFiltersCount());
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Countries"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </FormControl>
                    )}

                    {/* Network Type Dropdown */}
                    {(showNetworkTypeFilter && <FormControl fullWidth>
                        <Autocomplete
                            fullWidth
                            options={networkTypeOptions}
                            getOptionLabel={(option) => option.networkType}
                            value={networkTypeOptions.find((opt) => opt.id === selectedNetworkType) || null}
                            onChange={(event, newValue) => {
                                setSelectedNetworkType(newValue ? newValue.id : "");
                                onFiltersCountChange?.(getAppliedFiltersCount());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select network type"
                                    variant="outlined"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleApply();
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormControl>)}

                    {/* Transaction Type Dropdown */}
                    {showTxnTypeFilter && (
                        <FormControl fullWidth>
                            <Autocomplete
                                fullWidth
                                options={txnTypeOptions}
                                getOptionLabel={(option) => option.name}
                                value={txnTypeOptions.find((opt) => opt.id === selectedTxnType) || null}
                                onChange={(event, newValue) => {
                                    setSelectedTxnType(newValue ? newValue.id : "");
                                    onFiltersCountChange?.(getAppliedFiltersCount());
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Transaction Type"
                                        variant="outlined"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleApply();
                                            }
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    )}

                    {/* Date Time Range */}
                    {showDateTimeRangeFilter && (
                        <Box sx={{ position: "relative", mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Select Date & Time Range"
                                value={
                                    dateRange
                                        ? `${dateRange.startDate?.toLocaleDateString()} ${startTime} - ${dateRange.endDate?.toLocaleDateString()} ${endTime}`
                                        : ""
                                }
                                onClick={() => setDateTimeOpen(!dateTimeOpen)}
                                placeholder="Select date & time range"
                                InputProps={{
                                    endAdornment: dateRange ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClearDateTime();
                                                }}
                                                edge="end"
                                                size="small"
                                                aria-label="Clear date time range"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            {dateTimeOpen && (
                                <Box
                                    ref={dateTimeRef}
                                    sx={{
                                        position: "absolute",
                                        zIndex: 9999,
                                        top: "62px",
                                        left: 0,
                                        backgroundColor: "#fff",
                                        boxShadow: 3,
                                    }}
                                >
                                    <DateRangePicker
                                        ranges={[dateRange || { startDate: new Date(), endDate: new Date(), key: "selection" }]}
                                        onChange={handleSelect}
                                        months={1}
                                        minDate={new Date(2024, 0, 1)}
                                        maxDate={addDays(new Date(), 30)}
                                        direction="horizontal"
                                    />

                                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            value={startTime}
                                            onChange={(e) => setStartTime(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                    </Box>
                                    {/* Select button */}
                                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            disabled={!dateRange}
                                            onClick={() => setDateTimeOpen(false)}
                                        >
                                            Select
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* User name Dropdown */}
                    {(showUserNameFilter && <FormControl fullWidth>
                        <Autocomplete
                            fullWidth
                            options={userNameOptions}
                            getOptionLabel={(option) =>
                                option.email && option.name
                                    ? `${option.name} - ${option.email}`
                                    : option.email && !option.name
                                        ? option.email
                                        : option.name
                            }
                            value={userNameOptions.find((opt) => opt.id === selectedUserId) || null}
                            onChange={(event, newValue) => {
                                setselectedUserId(newValue ? newValue.id : "");
                                onFiltersCountChange?.(getAppliedFiltersCount());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select User"
                                    variant="outlined"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleApply();
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormControl>)}

                    {/* Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "space-around", mt: "auto" }}>
                        <StyledButton variant="contained" onClick={handleClear}>
                            Clear All
                        </StyledButton>
                        <StyledButton variant="contained" onClick={handleApply}>
                            Apply
                        </StyledButton>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default ReusableDrawer;
